'use client'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import {
  ChevronRight,
  Paperclip,
  Folder,
  Activity,
  ArrowRight
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { getRedmineBaseUrl } from '@/lib/tenant-config'

interface ActivityDetail {
  property: string
  name: string
  old_value?: string
  new_value?: string
}

interface ActivityItem {
  id: number
  type?: string
  title?: string
  description?: string
  notes?: string
  details?: ActivityDetail[]
  user?: {
    id: number
    name: string
  }
  created_on: string
  project?: {
    id: number
    name: string
  }
  issue?: {
    id: number
    subject: string
  }
}

interface ActivityFeedProps {
  activities: ActivityItem[]
  limit?: number
  showViewAll?: boolean
  viewAllLink?: string
  emptyMessage?: string
  trackers?: Array<{ id: number; name: string }>
  statuses?: Array<{ id: number; name: string; is_closed: boolean }>
  priorities?: Array<{ id: number; name: string }>
  users?: Array<{ id: number; name?: string; firstname?: string; lastname?: string; login?: string }>
  compact?: boolean
}

export function ActivityFeed({
  activities,
  limit,
  showViewAll = false,
  viewAllLink = '/activity',
  emptyMessage = 'No recent activity',
  trackers = [],
  statuses = [],
  priorities = [],
  users = [],
  compact = false
}: ActivityFeedProps) {
  const t = useTranslations()
  
  const displayActivities = limit ? activities.slice(0, limit) : activities

  // Helper function to get readable field names
  const getReadableFieldName = (fieldName: string): string => {
    const fieldMap: Record<string, string> = {
      status_id: 'Status',
      priority_id: 'Priority',
      assigned_to_id: 'Assignee',
      tracker_id: 'Tracker',
      done_ratio: '% Done',
      estimated_hours: 'Estimated Time',
      start_date: 'Start Date',
      due_date: 'Due Date',
      parent_id: 'Parent Task',
      description: 'Description',
      subject: 'Subject'
    }
    return fieldMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  // Helper function to get readable values
  const getReadableValue = (property: string, name: string, value: string | undefined): string => {
    if (!value) return ''

    if (name === 'tracker_id') {
      const tracker = trackers.find(t => t.id === parseInt(value))
      return tracker?.name || value
    }

    if (name === 'status_id') {
      const status = statuses.find(s => s.id === parseInt(value))
      return status?.name || value
    }

    if (name === 'priority_id') {
      const priority = priorities.find(p => p.id === parseInt(value))
      return priority?.name || value
    }

    if (name === 'assigned_to_id') {
      const user = users.find(u => u.id === parseInt(value))
      if (user) {
        const userName = user.name ||
                        (user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : null) ||
                        user.login ||
                        `User #${value}`
        return userName
      }
      return `User #${value}`
    }

    if (name === 'done_ratio') {
      return `${value}%`
    }

    return value
  }

  // Helper function to check if detail is an attachment
  const isAttachment = (detail: ActivityDetail): boolean => {
    return detail.property === 'attachment' && !!detail.new_value
  }

  // Helper function to get attachment URL
  const getAttachmentUrl = (attachmentId: string, issueId: number): string => {
    const baseUrl = getRedmineBaseUrl()
    return `${baseUrl}/attachments/download/${attachmentId}/${encodeURIComponent(attachmentId)}`
  }

  if (displayActivities.length === 0) {
    return (
      <div className="text-center py-12">
        <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <h3 className="font-medium mb-1">{emptyMessage || t('dashboard.noRecentActivity')}</h3>
        <p className="text-sm text-muted-foreground">{t('dashboard.activityPlaceholder')}</p>
      </div>
    )
  }

  return (
    <>
      <div className={`relative ${compact ? 'space-y-3' : 'space-y-6'} before:absolute ${compact ? 'before:left-[15px]' : 'before:left-[19px]'} before:top-2 before:bottom-2 before:w-[2px] before:bg-border`}>
        {displayActivities.map((activity, index) => (
          <div key={activity.id || index} className={`relative ${compact ? 'pl-8' : 'pl-12'}`}>
            {/* Timeline dot */}
            <div className={`absolute left-0 top-1 ${compact ? 'w-8 h-8' : 'w-10 h-10'} rounded-full flex items-center justify-center`}>
              <div className={`${compact ? 'w-2 h-2' : 'w-2.5 h-2.5'} rounded-full bg-primary`}></div>
            </div>

            {/* Content */}
            <div className={compact ? 'space-y-1' : 'space-y-2'}>
              {/* Header */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>{activity.user?.name || t('dashboard.unknownUser')}</span>
                  {!compact && (activity.notes ? (
                    <Badge variant="secondary" className="text-xs px-2 py-0">
                      {t('dashboard.commented')}
                    </Badge>
                  ) : activity.details && activity.details.length > 0 ? (
                    <Badge variant="outline" className="text-xs px-2 py-0">
                      {t('dashboard.updatedWord')}
                    </Badge>
                  ) : null)}
                </div>
                <time className={`${compact ? 'text-[10px]' : 'text-xs'} text-muted-foreground whitespace-nowrap`}>
                  {compact ? (
                    new Date(activity.created_on).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })
                  ) : (
                    new Date(activity.created_on).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  )}
                </time>
              </div>

              {activity.issue && (
                <Link
                  href={`/issues/${activity.issue.id}`}
                  className={`${compact ? 'text-xs' : 'text-sm'} text-primary hover:underline block truncate`}
                >
                  #{activity.issue.id}: {activity.issue.subject}
                </Link>
              )}

              {/* Comment - hide in compact mode if too long */}
              {activity.notes && !compact && (
                <div className="bg-muted/50 rounded-lg p-3 text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none">
                  {activity.notes}
                </div>
              )}
              
              {/* Comment - truncated in compact mode */}
              {activity.notes && compact && activity.notes.length > 60 && (
                <div className="bg-muted/50 rounded-lg p-2 text-xs leading-relaxed">
                  {activity.notes.substring(0, 60)}...
                </div>
              )}
              
              {activity.notes && compact && activity.notes.length <= 60 && (
                <div className="bg-muted/50 rounded-lg p-2 text-xs leading-relaxed">
                  {activity.notes}
                </div>
              )}

              {/* Field changes */}
              {activity.details && activity.details.length > 0 && (
                <div className={`${compact ? 'space-y-0.5' : 'space-y-1.5'} pt-1`}>
                  {activity.details.slice(0, compact ? 2 : undefined).map((detail: ActivityDetail, idx: number) => {
                    // Check if this is an attachment
                    if (isAttachment(detail)) {
                      const filename = detail.new_value
                      const attachmentId = detail.name
                      const downloadUrl = activity.issue ? getAttachmentUrl(attachmentId, activity.issue.id) : '#'

                      return (
                        <div key={idx} className={`flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
                          <Paperclip className={`${compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} text-muted-foreground`} />
                          <span className="text-muted-foreground">
                            <span className="font-medium text-foreground">{t('dashboard.attachmentAdded')}</span>
                            {!compact && (
                              <>
                                {' '}
                                <a
                                  href={downloadUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900 transition-colors underline"
                                >
                                  {filename}
                                </a>
                              </>
                            )}
                          </span>
                        </div>
                      )
                    }

                    // Handle description changes specially - too verbose
                    if (detail.name === 'description') {
                      const hasOldValue = detail.old_value && detail.old_value.trim()
                      const hasNewValue = detail.new_value && detail.new_value.trim()

                      return (
                        <div key={idx} className={`flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
                          <ChevronRight className={`${compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} text-muted-foreground`} />
                          <span className="text-muted-foreground">
                            <span className="font-medium text-foreground">{t('dashboard.description')}</span>
                              {hasOldValue && hasNewValue ? ` ${t('dashboard.updatedWord')}` : hasNewValue ? ` ${t('dashboard.addedWord')}` : ` ${t('dashboard.removedWord')}`}
                          </span>
                        </div>
                      )
                    }

                    // Regular field changes
                    const oldValue = getReadableValue(detail.property, detail.name, detail.old_value)
                    const newValue = getReadableValue(detail.property, detail.name, detail.new_value)

                    return (
                      <div key={idx} className={`flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
                        <ChevronRight className={`${compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} text-muted-foreground`} />
                        <span className="text-muted-foreground truncate">
                          <span className="font-medium text-foreground">{getReadableFieldName(detail.name)}</span>
                          {oldValue && newValue ? (
                            <>
                              {!compact && (
                                <>
                                  {' '}{t('dashboard.changedFrom')}{' '}
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300 line-through">
                                    {oldValue}
                                  </span>
                                  {' '}{t('dashboard.toWord')}{' '}
                                </>
                              )}
                              {compact && ' → '}
                              <span className={`inline-flex items-center ${compact ? 'px-1.5 py-0' : 'px-2 py-0.5'} rounded text-xs bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300`}>
                                {newValue}
                              </span>
                            </>
                          ) : newValue ? (
                            <>
                              {' '}{t('dashboard.setTo')}{' '}
                              <span className={`inline-flex items-center ${compact ? 'px-1.5 py-0' : 'px-2 py-0.5'} rounded text-xs bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300`}>
                                {newValue}
                              </span>
                            </>
                          ) : oldValue && !compact ? (
                            <>
                              {' '}{t('dashboard.removedWord')}{' '}
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted text-muted-foreground line-through">
                                {oldValue}
                              </span>
                            </>
                          ) : null}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Project */}
              {activity.project && !compact && (
                <div className="flex items-center gap-1.5 mt-1">
                  <Folder className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{activity.project.name}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showViewAll && limit && activities.length > limit && (
        <div className="mt-6 pt-4 border-t">
          <Link href={viewAllLink}>
            <Button variant="ghost" size="sm" className="w-full">
              {t('dashboard.viewAllActivity')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      )}
    </>
  )
}
